import { StaticPageInterceptorContext, StaticPages, StaticPagesContext } from "@amzn/aws-tc-xb-xps";
import { PageCode, useLogger, UseStaticPageHookContext } from "@amzn/xb-hooks";
import { PropsWithChildren, useContext } from "react";

/**
 * Relying on these error pages is not recommended. XB Applications should provide their own error pages.
 * These defaults are provided for non-production purposes only.
 */
const createDefaultPageComponent = (message: string, isError: boolean) => {
  const DefaultStaticPage = ({ pageCode }: PropsWithChildren<{ pageCode: PageCode }>) => {
    const { warn } = useLogger();
    warn(`Default static page component is not recommended for production`, message, isError);
    return (
      <>
        <h1>{isError ? `Error: ${pageCode.substring(1)}` : pageCode}</h1>
        <p>{message}</p>
      </>
    );
  };
  return DefaultStaticPage;
};

export const UseStaticPageHookProvider = ({
  children,
  pages = {},
}: PropsWithChildren<{ pages?: Partial<StaticPages> }>) => {
  // default all pages that were not provided by the application
  const {
    E400 = createDefaultPageComponent("Bad Request", true),
    E403 = createDefaultPageComponent("Forbidden", true),
    E404 = createDefaultPageComponent("Page Not Found", true),
    E408 = createDefaultPageComponent("Request Timeout", true),
    E500 = createDefaultPageComponent("Internal Server Error", true),
    Maintenance = createDefaultPageComponent(
      "We’re doing scheduled maintenance and expect this page to be available again soon. Thank you for your patience.",
      false,
    ),
  } = pages;

  const useStaticPage = () => {
    const callback = useContext(StaticPageInterceptorContext);
    return {
      displayStaticPage: callback,
      display400: () => callback("E400"),
      display403: () => callback("E403"),
      display404: () => callback("E404"),
      display408: () => callback("E408"),
      display500: () => callback("E500"),
      displayDownForMaintenance: () => callback("Maintenance"),
    };
  };
  return (
    <>
      <StaticPagesContext.Provider value={{ E400, E403, E404, E408, E500, Maintenance }}>
        <UseStaticPageHookContext.Provider value={useStaticPage}>{children}</UseStaticPageHookContext.Provider>
      </StaticPagesContext.Provider>
    </>
  );
};
